import { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    FullScreenSpinner,
    ScreenContainer,
    SegmentedControl,
    View,
} from 'smoothie';

import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../../../logger/log-rocket/LogRocketFacade';
import useGetAllStatements from '../../../hooks/useGetAllStatements';
import useGetAllTaxDocuments from '../../../hooks/useGetAllTaxDocuments';
import Statements from './Statements';
import TaxDocuments from './TaxDocuments';

enum DocumentType {
    STATEMENT = 'statement',
    TAX = 'tax',
}

const Documents = () => {
    LogRocketFacade.tagPage('Documents');

    const user = useAuthedUser();

    const [index, setIndex] = useState(0);
    const [documentType, setDocumentType] = useState(DocumentType.STATEMENT);

    const routes = Object.values(DocumentType).map((step) => ({ key: step }));

    const { data: statements, isLoading: isLoadingStatements } =
        useGetAllStatements({
            userId: user.id,
        });

    const { data: taxDocuments, isLoading: isLoadingTaxDocuments } =
        useGetAllTaxDocuments({
            userId: user.id,
        });

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: DocumentType } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        switch (route.key) {
            case DocumentType.STATEMENT:
                return <Statements statements={statements ?? []} />;
            case DocumentType.TAX:
                return <TaxDocuments taxDocuments={taxDocuments ?? []} />;
            default:
                return <></>;
        }
    };

    const isLoading = isLoadingStatements || isLoadingTaxDocuments;

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer
            title="Documents"
            hasBackButton
            containerProps={{ testID: 'documents-page' }}
        >
            <View
                height="100%"
                $sm={{ width: 500, marginTop: 32, alignSelf: 'center' }}
            >
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderTabBar={() => (
                        <View marginVertical={16}>
                            <SegmentedControl
                                options={[
                                    {
                                        label: 'Statements',
                                        value: DocumentType.STATEMENT,
                                    },
                                    {
                                        label: 'Taxes',
                                        value: DocumentType.TAX,
                                    },
                                ]}
                                selected={documentType}
                                defaultValue={DocumentType.STATEMENT}
                                onValueChange={(documentType) => {
                                    setDocumentType(documentType);
                                    setIndex(
                                        documentType === DocumentType.STATEMENT
                                            ? 0
                                            : 1,
                                    );
                                }}
                            />
                        </View>
                    )}
                    renderScene={renderScene}
                    onIndexChange={setIndex}
                />
            </View>
        </ScreenContainer>
    );
};

export default Documents;
